'use client';

import { CaretUp } from 'assets/icons';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { Container } from 'components';
import { Hero } from './HeroCatSection';
import { ButtonRows, ButtonTabs } from './StyledConstants';
import styles from './PartnerCategories.module.css';

export const PartnerCategoriesMap = {
  RESEARCH: 'RESEARCH',
  HEALTHCARE: 'HEALTHCARE',
  SPORTS: 'SPORTS',
  FITNESS: 'FITNESS',
  COMPANIES: 'COMPANIES',
};

const partnerCategories: {
  [key: string]: { title: string; children?: React.ReactNode };
} = {
  [PartnerCategoriesMap.RESEARCH]: {
    title: 'Research Institutions',
    children: (
      <>
        <Hero
          title="Continuous data for rigorous research"
          rootClass="padding"
          categoryTitle="Research Institutions"
          img={{
            desktopSrc: 'research.png',
            mobileSrc: 'research.png',
            alt: 'Athletes',
            width: 4808,
            height: 2888,
            caption:
              'Ultrahuman enables researchers to collect high-resolution, longitudinal biomarker data in real-world settings. Designed to support academic studies across sleep, metabolism, recovery, and human performance.',
            captiionAlignment: 'right',
          }}
          cta={{
            link: '/ring/buy',
            text: 'Become a partner',
          }}
        />
      </>
    ),
  },
  [PartnerCategoriesMap.HEALTHCARE]: {
    title: 'Healthcare Providers',
    children: (
      <>
        <Hero
          title="Monitor health insights beyond the clinic"
          rootClass="padding"
          categoryTitle="Healthcare Providers"
          img={{
            desktopSrc: 'healthcare.png',
            mobileSrc: 'healthcare.png',
            alt: 'healthcare',
            width: 4808,
            height: 2888,
            caption:
              'Work with real-world biomarker data to support prevention, monitoring, and personalised care. Ultrahuman integrates seamlessly into modern healthcare and wellness delivery models.',
            captiionAlignment: 'right',
          }}
          cta={{
            link: '/ring/buy',
            text: 'Become a partner',
          }}
        />
      </>
    ),
  },
  [PartnerCategoriesMap.SPORTS]: {
    title: 'Sports Teams',
    children: (
      <>
        <Hero
          title="Performance insights for elite competition"
          rootClass="padding"
          categoryTitle="Sports Teams"
          img={{
            desktopSrc: 'sports.png',
            mobileSrc: 'sports.png',
            alt: 'Sports Teams',
            width: 4808,
            height: 2888,
            caption:
              'Support athletes with continuous data on recovery, readiness, and physiological load. Ultrahuman helps teams make informed decisions across training, travel, and competition cycles.',
            captiionAlignment: 'right',
          }}
          cta={{
            link: '/ring/buy',
            text: 'Become a partner',
          }}
        />
      </>
    ),
  },
  [PartnerCategoriesMap.FITNESS]: {
    title: 'Gyms & Fitness Clubs',
    children: (
      <>
        <Hero
          title="Data-driven training experiences"
          rootClass="padding"
          categoryTitle="Gyms & Fitness Clubs"
          img={{
            desktopSrc: 'fitness.png',
            mobileSrc: 'fitness.png',
            alt: 'Gyms & Fitness Clubs',
            width: 4808,
            height: 2888,
            caption:
              'Enhance member engagement with insights into recovery, strain, and adaptation. Ultrahuman brings objective physiology into coaching, programming, and member retention.',
            captiionAlignment: 'right',
          }}
          cta={{
            link: '/ring/buy',
            text: 'Become a partner',
          }}
        />
      </>
    ),
  },
  [PartnerCategoriesMap.COMPANIES]: {
    title: 'Companies',
    children: (
      <>
        <Hero
          title="Health insights at an organisational level"
          rootClass="padding"
          categoryTitle="Companies"
          img={{
            desktopSrc: 'company.png',
            mobileSrc: 'company.png',
            alt: 'Companies',
            width: 4808,
            height: 2888,
            caption:
              'Empower teams with continuous visibility into stress, recovery, and wellbeing. Ultrahuman supports modern workplace health initiatives with privacy-first, scalable infrastructure.',
            captiionAlignment: 'right',
          }}
          cta={{
            link: '/ring/buy',
            text: 'Become a partner',
          }}
        />
      </>
    ),
  },
};
export const PartnerCategories = forwardRef(
  ({ className }: { className?: string }, ref: React.Ref<HTMLElement>) => {
    const [active, setActive] = useState(
      () => Object.keys(partnerCategories)[0],
    );
    const [displayedContent, setDisplayedContent] = useState(
      () => Object.keys(partnerCategories)[0],
    );
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showLeftCaret, setShowLeftCaret] = useState(false);
    const [showRightCaret, setShowRightCaret] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRowsRef = useRef<HTMLDivElement>(null);

    const checkOverflow = () => {
      if (buttonRowsRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = buttonRowsRef.current;
        setShowLeftCaret(scrollLeft > 0);
        setShowRightCaret(
          scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth,
        );
      }
    };

    useEffect(() => {
      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }, []);

    const scroll = (direction: 'left' | 'right') => {
      if (buttonRowsRef.current) {
        const scrollAmount = 200;
        buttonRowsRef.current.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth',
        });
      }
    };

    const handleScroll = () => {
      checkOverflow();
    };

    return (
      <section
        ref={ref}
        className={`${styles.root}${className ? ` ${className}` : ''}`}
      >
        <Container ref={containerRef}>
          <div className={styles.buttonRowsWrapper}>
            {showLeftCaret && (
              <button
                className={`${styles.scrollButton} ${styles.left}`}
                onClick={() => scroll('left')}
                aria-label="Scroll left"
              >
                <CaretUp />
              </button>
            )}
            <ButtonRows ref={buttonRowsRef} onScroll={handleScroll}>
              {Object.keys(partnerCategories).map((key) => (
                <ButtonTabs
                  className={key === active ? styles.active : ''}
                  key={key}
                  onClick={(e) => {
                    if (key !== active) {
                      setActive(key);
                      setIsTransitioning(true);
                      setTimeout(() => {
                        setDisplayedContent(key);
                        setTimeout(() => {
                          setIsTransitioning(false);
                        }, 50);
                      }, 300);
                    }
                    if (window.innerWidth > 769) return;
                    (e.target as HTMLButtonElement).scrollIntoView({
                      behavior: 'smooth',
                      inline: 'center',
                      block: 'start',
                    });
                  }}
                >
                  {partnerCategories[key].title}
                </ButtonTabs>
              ))}
            </ButtonRows>
            {showRightCaret && (
              <button
                className={`${styles.scrollButton} ${styles.right}`}
                onClick={() => scroll('right')}
                aria-label="Scroll right"
              >
                <CaretUp />
              </button>
            )}
          </div>
          <div
            className={`${styles.content} ${
              isTransitioning ? styles.fadeOut : styles.fadeIn
            }`}
          >
            {partnerCategories[displayedContent].children}
          </div>
          {/* Hidden preload containers */}
          <div style={{ display: 'none' }}>
            {Object.keys(partnerCategories).map((key) =>
              key !== displayedContent ? (
                <div key={key}>{partnerCategories[key].children}</div>
              ) : null,
            )}
          </div>
        </Container>
      </section>
    );
  },
);

PartnerCategories.displayName = 'PartnerCategories';
